import { useObject, useRecordList, useRecordsByIds } from '../../data/hooks';
import { Button, SelectMenu, Skeleton } from '../../ui';
import { useFieldContext } from '../FieldContext';

/** How many target records the picker offers before search becomes necessary. */
const PICKER_LIMIT = 50;

/**
 * A picker over the target object's records, labelled by the target's
 * label-identifier field. Above `PICKER_LIMIT` records this needs a searching
 * picker; that is a Phase-5 job and the boundary is marked rather than hidden.
 */
export function RelationInput() {
  const { field, value, onChange, readOnly } = useFieldContext();
  const targetObjectId = field.options?.relation?.targetObjectId;

  const object = useObject(targetObjectId ?? '');
  const identifier = object.data?.labelIdentifierFieldId;
  const list = useRecordList(targetObjectId ?? '', {
    select: identifier === undefined ? [] : [identifier],
    limit: PICKER_LIMIT,
    enabled: targetObjectId !== undefined && identifier !== undefined,
  });
  const { records } = useRecordsByIds(targetObjectId ?? '', list.data?.ids ?? []);

  if (targetObjectId === undefined) {
    return <p className="text-xs text-danger">This relation field has no target object.</p>;
  }
  if (identifier === undefined || list.isPending) {
    return <Skeleton className="h-10 w-full" shape="block" />;
  }

  const options = records.map((record) => {
    const label = record.values[identifier];
    return {
      value: record.id,
      /**
       * "Untitled", never the uuid (audit P1-9). Wave 1's `RelationCellInput` already
       * refused to reproduce this fallback in a new file and said so in its ledger; this
       * is the older half of that disagreement, resolved the same way. A menu of uuids is
       * a menu nobody can choose from, and the word is what the rest of the product calls
       * exactly this record.
       */
      label: typeof label === 'string' && label.trim() !== '' ? label : 'Untitled',
    };
  });

  return (
    <div className="flex items-center gap-2">
      <SelectMenu
        value={typeof value === 'string' && value.length > 0 ? value : undefined}
        onValueChange={onChange}
        options={options}
        disabled={readOnly}
        ariaLabel={field.label}
        placeholder={`Select ${object.data?.labelSingular ?? 'record'}`}
      />
      {!field.isRequired && typeof value === 'string' && value.length > 0 && (
        <Button variant="ghost" size="sm" onClick={() => onChange(null)} disabled={readOnly}>
          Clear
        </Button>
      )}
    </div>
  );
}
